import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Calendar, Plus, Edit, Trash2, CheckCircle, Clock, AlertCircle, Link as LinkIcon, TrendingUp, BarChart3, Target } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import BackNavigation from "../components/BackNavigation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ProjectPlanning = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [activities, setActivities] = useState([]);
  const [boqItems, setBoqItems] = useState([]);
  const [dprData, setDprData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [viewMode, setViewMode] = useState("gantt"); // "list" or "gantt"
  const [showDprOverlay, setShowDprOverlay] = useState(true);
  
  const [form, setForm] = useState({
    activity_name: "",
    description: "",
    boq_item_id: "",
    planned_start_date: "",
    planned_end_date: "",
    planned_quantity: 0,
    unit: "",
    priority: "Medium",
    milestone: false,
    estimated_cost: 0
  });

  const currentUser = getUser() || {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, planningRes, boqRes, dprRes] = await Promise.all([
        apiClient.get(`/projects/${projectId}`),
        apiClient.get(`/planning/${projectId}`),
        apiClient.get(`/boq/${projectId}`),
        apiClient.get(`/dpr/${projectId}`)
      ]);
      setProject(projectRes.data);
      setBoqItems(boqRes.data);
      setDprData(dprRes.data || []);
      
      // Calculate actual progress from DPR for each activity
      const activitiesWithProgress = calculateDprProgress(planningRes.data, boqRes.data, dprRes.data || []);
      setActivities(activitiesWithProgress);
    } catch (error) {
      toast.error("Failed to load planning data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate actual progress from DPR data
  const calculateDprProgress = (planningData, boqData, dprList) => {
    return planningData.map(activity => {
      // Find the linked BOQ item
      const boqItem = boqData.find(b => b.id === activity.boq_item_id);
      if (!boqItem) return { ...activity, actual_progress: 0, dpr_entries: [], schedule_status: "Not Started" };
      
      // Find all DPR entries for this BOQ item
      const relatedDprs = dprList.filter(dpr => 
        dpr.items?.some(item => item.boq_item_id === activity.boq_item_id)
      );
      
      // Sum up actual quantities from DPR
      let totalActualQuantity = 0;
      const dprEntries = [];
      
      relatedDprs.forEach(dpr => {
        const dprItem = dpr.items?.find(item => item.boq_item_id === activity.boq_item_id);
        if (dprItem) {
          totalActualQuantity += dprItem.quantity_today || 0;
          dprEntries.push({
            date: dpr.date,
            quantity: dprItem.quantity_today,
            cumulative: dprItem.quantity_cumulative
          });
        }
      });
      
      // Calculate progress percentage
      const plannedQty = activity.planned_quantity || boqItem.quantity || 1;
      const actualProgress = Math.min(100, (totalActualQuantity / plannedQty) * 100);
      
      // Determine schedule status
      const today = new Date();
      const endDate = new Date(activity.planned_end_date);
      const startDate = new Date(activity.planned_start_date);
      const totalDuration = (endDate - startDate) / (1000 * 60 * 60 * 24);
      const elapsedDays = Math.max(0, (today - startDate) / (1000 * 60 * 60 * 24));
      const expectedProgress = Math.min(100, (elapsedDays / totalDuration) * 100);
      
      let scheduleStatus = "On Track";
      if (today > endDate && actualProgress < 100) {
        scheduleStatus = "Delayed";
      } else if (actualProgress >= expectedProgress + 10) {
        scheduleStatus = "Ahead";
      } else if (actualProgress < expectedProgress - 10) {
        scheduleStatus = "Behind";
      } else if (actualProgress >= 100) {
        scheduleStatus = "Completed";
      } else if (today < startDate) {
        scheduleStatus = "Not Started";
      }
      
      return {
        ...activity,
        actual_progress: actualProgress,
        expected_progress: expectedProgress,
        actual_quantity: totalActualQuantity,
        dpr_entries: dprEntries,
        schedule_status: scheduleStatus,
        boq_item_code: boqItem.item_code
      };
    });
  };

  const resetForm = () => {
    setForm({
      activity_name: "",
      description: "",
      boq_item_id: "",
      planned_start_date: "",
      planned_end_date: "",
      planned_quantity: 0,
      unit: "",
      priority: "Medium",
      milestone: false,
      estimated_cost: 0
    });
    setEditingActivity(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingActivity) {
        await apiClient.put(`/planning/${editingActivity.id}`, form);
        toast.success("Activity updated");
      } else {
        await apiClient.post("/planning", { ...form, project_id: projectId });
        toast.success("Activity created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save activity");
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setForm({
      activity_name: activity.activity_name,
      description: activity.description || "",
      boq_item_id: activity.boq_item_id || "",
      planned_start_date: activity.planned_start_date,
      planned_end_date: activity.planned_end_date,
      planned_quantity: activity.planned_quantity || 0,
      unit: activity.unit || "",
      priority: activity.priority || "Medium",
      milestone: activity.milestone || false,
      estimated_cost: activity.estimated_cost || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await apiClient.delete(`/planning/${activityId}`);
      toast.success("Activity deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete activity");
    }
  };

  const updateStatus = async (activityId, status) => {
    try {
      await apiClient.put(`/planning/${activityId}`, { status });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      "Not Started": "bg-slate-200 text-slate-600",
      "In Progress": "bg-primary/20 text-primary",
      "Completed": "bg-success/20 text-success",
      "Delayed": "bg-error/20 text-error",
      "On Hold": "bg-warning/20 text-warning"
    };
    return <span className={`px-2 py-1 rounded-sm text-xs font-medium ${config[status] || 'bg-slate-100'}`}>{status}</span>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      "Low": "bg-slate-100 text-slate-600",
      "Medium": "bg-primary/10 text-primary",
      "High": "bg-warning/20 text-warning",
      "Critical": "bg-error/20 text-error"
    };
    return <span className={`px-2 py-1 rounded-sm text-xs font-medium ${config[priority] || 'bg-slate-100'}`}>{priority}</span>;
  };

  // Render Gantt Chart with DPR Progress
  const renderGanttChart = () => {
    if (activities.length === 0) return <div className="text-center py-12 text-slate-500">No activities to display</div>;
    
    // Find date range
    const allDates = activities.flatMap(a => [new Date(a.planned_start_date), new Date(a.planned_end_date)]);
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1;
    
    // Generate month headers
    const months = [];
    let currentMonth = new Date(minDate);
    while (currentMonth <= maxDate) {
      months.push(new Date(currentMonth));
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    // Today's position
    const today = new Date();
    const todayOffset = Math.ceil((today - minDate) / (1000 * 60 * 60 * 24));
    const todayPercent = (todayOffset / totalDays) * 100;
    
    return (
      <div className="overflow-x-auto">
        <div className="min-w-[1400px]">
          {/* Legend */}
          <div className="flex items-center justify-between mb-4 px-2">
            <div className="flex items-center space-x-4 text-xs">
              <div className="flex items-center space-x-1">
                <div className="w-4 h-3 bg-slate-300 rounded-sm"></div>
                <span>Planned</span>
              </div>
              {showDprOverlay && (
                <>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-3 bg-success rounded-sm"></div>
                    <span>On Track / Ahead</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-3 bg-warning rounded-sm"></div>
                    <span>Behind Schedule</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-3 bg-error rounded-sm"></div>
                    <span>Delayed</span>
                  </div>
                </>
              )}
            </div>
            <label className="flex items-center space-x-2 text-sm cursor-pointer">
              <input 
                type="checkbox" 
                checked={showDprOverlay} 
                onChange={(e) => setShowDprOverlay(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span>Show DPR Progress</span>
            </label>
          </div>

          {/* Header */}
          <div className="flex border-b-2 border-slate-300">
            <div className="w-72 flex-shrink-0 p-2 font-medium text-xs uppercase tracking-widest bg-slate-100">Activity</div>
            <div className="w-20 flex-shrink-0 p-2 font-medium text-xs uppercase tracking-widest bg-slate-100 text-center">Status</div>
            <div className="flex-1 flex relative">
              {months.map((month, i) => (
                <div key={i} className="flex-1 p-2 text-center text-xs font-medium bg-slate-100 border-l border-slate-200">
                  {month.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
              ))}
            </div>
          </div>
          
          {/* Activities */}
          {activities.map((activity, idx) => {
            const startDate = new Date(activity.planned_start_date);
            const endDate = new Date(activity.planned_end_date);
            const startOffset = Math.max(0, Math.ceil((startDate - minDate) / (1000 * 60 * 60 * 24)));
            const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
            const leftPercent = (startOffset / totalDays) * 100;
            const widthPercent = (duration / totalDays) * 100;
            
            // Progress bar color based on schedule status
            const progressColor = activity.schedule_status === "Completed" ? "bg-success" :
                                  activity.schedule_status === "Ahead" ? "bg-success" :
                                  activity.schedule_status === "On Track" ? "bg-primary" :
                                  activity.schedule_status === "Behind" ? "bg-warning" :
                                  activity.schedule_status === "Delayed" ? "bg-error" : "bg-slate-400";
            
            const statusBadgeColor = {
              "Completed": "bg-success/20 text-success",
              "Ahead": "bg-success/20 text-success",
              "On Track": "bg-primary/20 text-primary",
              "Behind": "bg-warning/20 text-warning",
              "Delayed": "bg-error/20 text-error",
              "Not Started": "bg-slate-100 text-slate-500"
            }[activity.schedule_status] || "bg-slate-100 text-slate-500";
            
            return (
              <div key={activity.id} className={`flex border-b border-slate-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                <div className="w-72 flex-shrink-0 p-2 text-sm">
                  <div className="font-medium truncate">{activity.activity_name}</div>
                  <div className="text-xs text-slate-500 flex items-center space-x-2">
                    <span>{activity.boq_item_code || ''}</span>
                    {activity.actual_progress > 0 && (
                      <span className="text-primary font-mono">
                        {activity.actual_quantity?.toFixed(1)} / {activity.planned_quantity} {activity.unit}
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-20 flex-shrink-0 p-2 flex items-center justify-center">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadgeColor}`}>
                    {activity.schedule_status === "Not Started" ? "N/S" : 
                     activity.schedule_status === "On Track" ? "OK" :
                     activity.schedule_status?.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 relative h-14">
                  {/* Today marker */}
                  {todayPercent >= 0 && todayPercent <= 100 && (
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                      style={{ left: `${todayPercent}%` }}
                    />
                  )}
                  
                  {/* Planned bar (background) */}
                  <div 
                    className="absolute top-3 h-4 bg-slate-200 rounded-sm"
                    style={{ left: `${leftPercent}%`, width: `${Math.max(widthPercent, 1)}%` }}
                  />
                  
                  {/* Actual progress bar (foreground) */}
                  {showDprOverlay && activity.actual_progress > 0 && (
                    <div 
                      className={`absolute top-3 h-4 ${progressColor} rounded-sm transition-all duration-300`}
                      style={{ 
                        left: `${leftPercent}%`, 
                        width: `${Math.max((widthPercent * activity.actual_progress / 100), 0.5)}%` 
                      }}
                    />
                  )}
                  
                  {/* Progress label */}
                  <div 
                    className="absolute top-3 h-4 flex items-center px-1"
                    style={{ left: `${leftPercent}%`, width: `${Math.max(widthPercent, 5)}%` }}
                  >
                    <span className="text-xs font-mono font-bold text-white drop-shadow-sm">
                      {activity.actual_progress > 0 ? `${activity.actual_progress.toFixed(0)}%` : ''}
                    </span>
                  </div>
                  
                  {/* Date labels */}
                  <div 
                    className="absolute top-8 text-[10px] text-slate-500 font-mono"
                    style={{ left: `${leftPercent}%` }}
                  >
                    {activity.planned_start_date?.split('-').slice(1).join('/')}
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Today indicator label */}
          {todayPercent >= 0 && todayPercent <= 100 && (
            <div className="flex border-t border-slate-200">
              <div className="w-72 flex-shrink-0"></div>
              <div className="w-20 flex-shrink-0"></div>
              <div className="flex-1 relative h-6">
                <div 
                  className="absolute top-1 text-[10px] text-red-500 font-medium bg-white px-1"
                  style={{ left: `${todayPercent}%`, transform: 'translateX(-50%)' }}
                >
                  Today
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <BackNavigation />
      
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">PROJECT PLANNING</h1>
          <p className="text-slate-600 mt-2">{project?.name || "Project"}</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex border border-slate-300 rounded-sm overflow-hidden">
            <button 
              onClick={() => setViewMode("list")}
              className={`px-4 py-2 text-sm ${viewMode === "list" ? "bg-primary text-white" : "bg-white text-slate-600"}`}
            >
              List View
            </button>
            <button 
              onClick={() => setViewMode("gantt")}
              className={`px-4 py-2 text-sm ${viewMode === "gantt" ? "bg-primary text-white" : "bg-white text-slate-600"}`}
            >
              Gantt Chart
            </button>
          </div>
          <button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Activity</span>
          </button>
        </div>
      </div>

      {/* Stats with DPR Progress */}
      <div className="grid md:grid-cols-5 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Activities</p>
          <p className="font-heading font-black text-3xl text-primary font-mono">{activities.length}</p>
        </div>
        <div className="stat-card border-l-4 border-l-success">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">On Track / Ahead</p>
          <p className="font-heading font-black text-3xl text-success font-mono">
            {activities.filter(a => a.schedule_status === "On Track" || a.schedule_status === "Ahead" || a.schedule_status === "Completed").length}
          </p>
        </div>
        <div className="stat-card border-l-4 border-l-warning">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Behind Schedule</p>
          <p className="font-heading font-black text-3xl text-warning font-mono">
            {activities.filter(a => a.schedule_status === "Behind").length}
          </p>
        </div>
        <div className="stat-card border-l-4 border-l-error">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Delayed</p>
          <p className="font-heading font-black text-3xl text-error font-mono">
            {activities.filter(a => a.schedule_status === "Delayed").length}
          </p>
        </div>
        <div className="stat-card border-l-4 border-l-accent">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Avg Progress</p>
          <p className="font-heading font-black text-3xl text-accent font-mono">
            {activities.length > 0 ? (activities.reduce((sum, a) => sum + (a.actual_progress || 0), 0) / activities.length).toFixed(0) : 0}%
          </p>
        </div>
      </div>

      {/* View Content */}
      {viewMode === "gantt" ? (
        <div className="stat-card">
          <h2 className="font-heading font-bold text-lg mb-4">Gantt Chart</h2>
          {renderGanttChart()}
        </div>
      ) : (
        <div className="stat-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-300">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Activity</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">BOQ Item</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Duration</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Progress</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Priority</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {activities.map(activity => (
                <tr key={activity.id} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      {activity.milestone && <span className="text-warning">◆</span>}
                      <div>
                        <p className="font-medium text-sm">{activity.activity_name}</p>
                        <p className="text-xs text-slate-500">{activity.description?.slice(0, 50)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 font-mono text-sm">{activity.boq_item_code || "-"}</td>
                  <td className="py-3 px-4 text-sm">
                    <div>{activity.planned_start_date}</div>
                    <div className="text-slate-500">to {activity.planned_end_date}</div>
                    <div className="text-xs text-slate-400">{activity.duration_days} days</div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full" 
                        style={{ width: `${activity.progress_percentage || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-slate-500">{(activity.progress_percentage || 0).toFixed(0)}%</span>
                  </td>
                  <td className="py-3 px-4 text-center">{getPriorityBadge(activity.priority)}</td>
                  <td className="py-3 px-4 text-center">{getStatusBadge(activity.status)}</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <select 
                        value={activity.status}
                        onChange={(e) => updateStatus(activity.id, e.target.value)}
                        className="text-xs border rounded px-1 py-1"
                      >
                        <option value="Not Started">Not Started</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Delayed">Delayed</option>
                        <option value="On Hold">On Hold</option>
                      </select>
                      <button onClick={() => handleEdit(activity)} className="p-1 hover:bg-slate-200 rounded">
                        <Edit className="w-4 h-4 text-primary" />
                      </button>
                      <button onClick={() => handleDelete(activity.id)} className="p-1 hover:bg-red-100 rounded">
                        <Trash2 className="w-4 h-4 text-error" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {activities.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              No planning activities yet. Create your first activity.
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingActivity ? "EDIT ACTIVITY" : "ADD PLANNING ACTIVITY"}
            </DialogTitle>
          </DialogHeader>
          
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mt-2">
            <p className="text-xs text-blue-700">
              <strong>Tip:</strong> Link activities to BOQ items to automatically track progress from DPR entries. The Gantt chart will show actual vs planned progress.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Activity Name *</label>
                <input
                  type="text"
                  required
                  value={form.activity_name}
                  onChange={(e) => setForm({...form, activity_name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  placeholder="e.g., Earthwork Excavation, GSB Layer Construction"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  rows="2"
                  placeholder="Brief description of the activity scope"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-primary mb-2 uppercase tracking-widest font-bold">
                  🔗 Link to BOQ Item (Required for DPR Tracking)
                </label>
                <select
                  value={form.boq_item_id}
                  onChange={(e) => {
                    const item = boqItems.find(b => b.id === e.target.value);
                    setForm({
                      ...form, 
                      boq_item_id: e.target.value,
                      unit: item?.unit || form.unit,
                      planned_quantity: item?.quantity || form.planned_quantity,
                      activity_name: form.activity_name || item?.description?.slice(0, 50) || ''
                    });
                  }}
                  className="w-full px-3 py-2 border-2 border-primary/30 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm bg-primary/5"
                  data-testid="boq-item-select"
                >
                  <option value="">-- Select BOQ Item --</option>
                  {boqItems.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.item_code} - {item.description?.slice(0, 50)} ({item.quantity} {item.unit})
                    </option>
                  ))}
                </select>
                {!form.boq_item_id && (
                  <p className="text-xs text-warning mt-1">⚠️ Without BOQ link, DPR progress will not be automatically tracked</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Priority</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({...form, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="milestone"
                  checked={form.milestone}
                  onChange={(e) => setForm({...form, milestone: e.target.checked})}
                  className="rounded border-slate-300"
                />
                <label htmlFor="milestone" className="text-sm">Mark as Milestone</label>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Start Date *</label>
                <input
                  type="date"
                  required
                  value={form.planned_start_date}
                  onChange={(e) => setForm({...form, planned_start_date: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">End Date *</label>
                <input
                  type="date"
                  required
                  value={form.planned_end_date}
                  onChange={(e) => setForm({...form, planned_end_date: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Planned Quantity</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.planned_quantity}
                  onChange={(e) => setForm({...form, planned_quantity: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Unit</label>
                <input
                  type="text"
                  value={form.unit}
                  onChange={(e) => setForm({...form, unit: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Estimated Cost (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.estimated_cost}
                  onChange={(e) => setForm({...form, estimated_cost: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="milestone"
                  checked={form.milestone}
                  onChange={(e) => setForm({...form, milestone: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="milestone" className="text-sm">Mark as Milestone</label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary">
                {editingActivity ? "Update Activity" : "Create Activity"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectPlanning;
